import { DISPLAY_TIME_ZONE } from '@lions/contracts';
import {
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';

import { type FieldProps, FormField, splitFieldProps, useFormField } from './FormField';

export interface DatePickerProps extends FieldProps {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  /** Extra props for MUI X's DatePicker. */
  pickerProps?: Omit<MuiDatePickerProps, 'value' | 'onChange' | 'label' | 'slotProps'>;
}

function DatePickerControl({
  value,
  onChange,
  minDate,
  maxDate,
  pickerProps,
}: Omit<DatePickerProps, keyof FieldProps>) {
  const field = useFormField();
  return (
    <MuiDatePicker
      value={value}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      // Calendar days in South Africa (CLAUDE.md § Time).
      timezone={DISPLAY_TIME_ZONE}
      disabled={field.disabled}
      slotProps={{
        textField: {
          id: field.controlId,
          // MUI X knows small and medium; FormField's data-control-size sizes large fields.
          size: field.size === 'sm' ? 'small' : 'medium',
          error: field.invalid,
          required: field.required,
          fullWidth: true,
          // The label sits above (FormField); point the field group at it and at the hint.
          slotProps: {
            input: { 'aria-labelledby': field.labelId, 'aria-describedby': field.describedBy },
          },
        },
      }}
      {...pickerProps}
    />
  );
}

/** A date field (South African calendar days) with label, hint and error, sized like the others. */
export function DatePicker(props: DatePickerProps) {
  const { field, control } = splitFieldProps(props);
  return (
    <FormField {...field}>
      <DatePickerControl {...control} />
    </FormField>
  );
}
